import { brand } from "@pervasivemind/bi-components/brand";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Modal,
  Select,
  Tabs,
} from "@omnitok/ui";
import {
  AlertCircle,
  FileSpreadsheet,
  FolderOpen,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  Upload,
} from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";

import { useT } from "@/i18n";
import { ColorField, Disclosure } from "@pervasivemind/admin-ui";
import {
  isUploadKind,
  projectsApi,
  type ConnectionResponse,
  type DataSourceKind,
  type ProjectDataSourceReloadResponse,
  type ProjectUploadResponse,
} from "@/lib/builder-api";
import { formatRelative } from "@pervasivemind/dashboard-render";
import { resolveLocalized } from "@pervasivemind/dashboard-render";
import { BilingualInput } from "@/pages/builder/components/BilingualInput";
import { LoaderScheduleFields } from "@/pages/loader/LoaderScheduleFields";
import type {
  DisplayLabel,
  FilesConfig,
  MasterFilesConfig,
  Project,
} from "@/types";

export const SLUG_PATTERN = /^[a-z][a-z0-9_-]*$/;

// PR-1 builder reorg: the icon catalog (now expanded + searchable)
// lives in ``@/lib/project-icons``; re-exported here for the pages
// that historically imported it from this module.
import { IconPicker } from "@/components/IconPicker";
import { findIconComponent } from "@/lib/project-icons";
import {
  hasProjectName,
  parseProjectName,
  serializeProjectName,
} from "@/lib/project-name";

export { findIconComponent };

/**
 * CRUD page for projects — lets users create, edit, and delete project
 * groupings for their reports.
 */
export function ProjectsPage(): JSX.Element {
  const { t, locale } = useT();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const result = await projectsApi.list();
      setProjects(result.projects);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleDelete = async (project: Project): Promise<void> => {
    // Resolve project.name from a potential JSON-encoded ``LocalizedString``
    // so the confirm dialog never shows raw ``{"en":"…"}`` to the admin.
    const displayName =
      resolveLocalized(project.name as DisplayLabel, locale) || project.slug;
    if (!window.confirm(t("projects.deleteConfirm", { name: displayName })))
      return;
    try {
      await projectsApi.remove(project.slug);
      setProjects((cur) => cur.filter((p) => p.id !== project.id));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : t("common.error"));
    }
  };

  const handleCreated = (project: Project): void => {
    setCreateOpen(false);
    setProjects((cur) => [project, ...cur]);
  };

  const handleUpdated = (project: Project): void => {
    setEditing(null);
    setProjects((cur) => cur.map((p) => (p.id === project.id ? project : p)));
  };

  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 sm:px-6">
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">
            {t("projects.title")}
          </h1>
          <p className="mt-1 text-sm text-neutral-500">
            {t("projects.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            ghost
            size="sm"
            onClick={() => void load()}
            disabled={loading}
            leftIcon={
              <RefreshCw
                size={14}
                className={loading ? "animate-spin" : undefined}
              />
            }
          >
            <span className="sr-only">{t("common.retry")}</span>
          </Button>
          <Button
            variant="primary"
            leftIcon={<Plus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            {t("projects.new")}
          </Button>
        </div>
      </div>

      {error && (
        <Card variant="bordered" className="mb-4 border-error/40 bg-error/5">
          <CardBody>
            <div className="flex items-start gap-2 text-sm text-error-dark">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">{error}</div>
              <Button
                variant="danger"
                ghost
                size="xs"
                onClick={() => void load()}
              >
                {t("common.retry")}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {loading && projects.length === 0 ? (
        <Card variant="bordered">
          <CardBody>
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-neutral-500">
              <Spinner size={16} ariaLabel={t("common.loading")} />
              {t("common.loading")}
            </div>
          </CardBody>
        </Card>
      ) : projects.length === 0 ? (
        <Card variant="bordered">
          <CardBody>
            <div className="flex flex-col items-center gap-2 py-12 text-center text-sm text-neutral-500">
              <FolderOpen size={32} className="text-neutral-300" />
              <p>{t("projects.empty")}</p>
            </div>
          </CardBody>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onEdit={() => setEditing(project)}
              onDelete={() => void handleDelete(project)}
            />
          ))}
        </div>
      )}

      {/* Create modal */}
      <ProjectFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={handleCreated}
      />

      {/* Edit modal */}
      {editing && (
        <ProjectFormModal
          open
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={handleUpdated}
        />
      )}
    </div>
  );
}

// ── Project card ─────────────────────────────────────────

interface ProjectCardProps {
  project: Project;
  onEdit: () => void;
  onDelete: () => void;
}

function ProjectCard({
  project,
  onEdit,
  onDelete,
}: ProjectCardProps): JSX.Element {
  const { t, locale } = useT();
  const Icon = findIconComponent(project.icon);
  // ``project.name`` is a TEXT column that may carry either a plain
  // string (legacy) OR a JSON-encoded ``LocalizedString`` (post-i18n).
  // Resolve to the active locale so the card header never shows raw
  // ``{"en":"…","es":"…"}`` JSON.
  const resolvedName =
    resolveLocalized(project.name as DisplayLabel, locale) || project.slug;
  const resolvedDescription = resolveLocalized(
    project.description as DisplayLabel,
    locale,
  );
  return (
    <Card variant="bordered" hoverable className="flex h-full flex-col">
      <CardHeader>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              {Icon && (
                <Icon
                  size={16}
                  className="shrink-0 text-white"
                  style={
                    project.color
                      ? { color: project.color }
                      : undefined
                  }
                />
              )}
              {!Icon && project.color && (
                <span
                  className="inline-block h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: project.color }}
                />
              )}
              <h3 className="truncate text-base font-semibold text-white">
                {resolvedName}
              </h3>
            </div>
            <p className="mt-0.5 truncate font-mono text-xs text-white/60">
              {project.slug}
            </p>
          </div>
        </div>
      </CardHeader>
      <CardBody className="flex flex-1 flex-col gap-3">
        <p className="line-clamp-3 text-sm text-neutral-600">
          {resolvedDescription || (
            <span className="italic text-neutral-500">
              {t("projects.description")}
            </span>
          )}
        </p>
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-neutral-200 pt-3">
          <span className="text-xs text-neutral-500">
            {formatRelative(project.updated_at)}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              size="sm"
              ghost
              leftIcon={<Pencil size={14} />}
              onClick={onEdit}
            >
              {t("common.edit")}
            </Button>
            <Button
              size="sm"
              variant="danger"
              ghost
              leftIcon={<Trash2 size={14} />}
              onClick={onDelete}
            >
              {t("common.delete")}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

// ── Create / Edit modal ──────────────────────────────────

export interface ProjectFormModalProps {
  open: boolean;
  project?: Project;
  onClose: () => void;
  onSaved: (project: Project) => void;
}

type FormTab = "general" | "maestros" | "archivos" | "data" | "loader";

/**
 * Project create/edit modal. Exported (iter 27) so the unified
 * `BuilderListPage` can reuse it for inline ``[+ New Project]`` and
 * per-card ``[edit]`` actions — the dedicated ``/projects`` page no
 * longer ships in the sidebar but the modal logic remains here.
 *
 * Pass ``project`` to enter edit mode; omit for create mode.
 */
export function ProjectFormModal({
  open,
  project,
  onClose,
  onSaved,
}: ProjectFormModalProps): JSX.Element {
  const { t } = useT();
  const isEdit = Boolean(project);

  const [slug, setSlug] = useState(project?.slug ?? "");
  const [name, setName] = useState<DisplayLabel>(
    parseProjectName(project?.name),
  );
  const [description, setDescription] = useState(project?.description ?? "");
  const [color, setColor] = useState(project?.color ?? brand.primary.DEFAULT);
  const [icon, setIcon] = useState(project?.icon ?? "");
  const [dashboardEnabled, setDashboardEnabled] = useState(
    project?.dashboard_enabled ?? true,
  );
  const [maestrosEnabled, setMaestrosEnabled] = useState(
    project?.maestros_enabled ?? true,
  );
  const [filesEnabled, setFilesEnabled] = useState(
    project?.files_enabled ?? true,
  );
  // Per-project Maestros / Archivos configuration. Held as local state
  // so the user can flip between tabs without losing edits before
  // hitting Save.
  const [masterFilesConfig, setMasterFilesConfig] = useState<MasterFilesConfig>(
    () => project?.master_files_config ?? {},
  );
  const [filesConfig, setFilesConfig] = useState<FilesConfig>(
    () => project?.files_config ?? {},
  );
  const [activeTab, setActiveTab] = useState<FormTab>("general");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSlug(project?.slug ?? "");
      setName(project?.name ?? "");
      setDescription(project?.description ?? "");
      setColor(project?.color ?? brand.primary.DEFAULT);
      setIcon(project?.icon ?? "");
      setDashboardEnabled(project?.dashboard_enabled ?? true);
      setMaestrosEnabled(project?.maestros_enabled ?? true);
      setFilesEnabled(project?.files_enabled ?? true);
      setMasterFilesConfig(project?.master_files_config ?? {});
      setFilesConfig(project?.files_config ?? {});
      setActiveTab("general");
      setError(null);
      setSubmitting(false);
    }
  }, [open, project]);

  const trimmedSlug = slug.trim();
  const slugValid = SLUG_PATTERN.test(trimmedSlug) && trimmedSlug.length >= 2;
  const hasAnySection = dashboardEnabled || maestrosEnabled || filesEnabled;

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!slugValid || !hasProjectName(name) || !hasAnySection) return;
    setError(null);
    setSubmitting(true);
    try {
      let saved: Project;
      if (isEdit && project) {
        saved = await projectsApi.update(project.slug, {
          slug: trimmedSlug,
          name: serializeProjectName(name),
          description: description.trim(),
          color: color.trim() || undefined,
          icon,
          dashboard_enabled: dashboardEnabled,
          maestros_enabled: maestrosEnabled,
          files_enabled: filesEnabled,
          master_files_config: masterFilesConfig,
          files_config: filesConfig,
        });
      } else {
        saved = await projectsApi.create({
          slug: trimmedSlug,
          name: serializeProjectName(name),
          description: description.trim() || undefined,
          color: color.trim() || undefined,
          icon: icon || undefined,
          dashboard_enabled: dashboardEnabled,
          maestros_enabled: maestrosEnabled,
          files_enabled: filesEnabled,
          master_files_config: masterFilesConfig,
          files_config: filesConfig,
        });
      }
      onSaved(saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { id: "general", label: t("projects.form.tab.general") },
    { id: "maestros", label: t("projects.form.tab.maestros") },
    { id: "archivos", label: t("projects.form.tab.archivos") },
    // Iter 23 — only visible when editing an existing project; data
    // source CRUD has its own endpoints and isn't part of the general
    // create flow, so wiring it on Create would just confuse.
    ...(isEdit
      ? [{ id: "data", label: t("projects.form.tab.data") }]
      : []),
    // Iter 29 — Loader tab, also edit-only. Hosts the cron schedule
    // moved over from the portal's deprecated admin Operations tab.
    // Disabled on create because the loader needs a data_source to
    // act on (chicken-and-egg: configure source first, then schedule).
    ...(isEdit
      ? [{ id: "loader", label: t("projects.form.tab.loader") }]
      : []),
  ];

  return (
    <Modal
      open={open}
      onClose={() => (!submitting ? onClose() : undefined)}
      title={isEdit ? t("common.edit") : t("projects.new")}
      size="full"
      footer={
        <>
          <Button
            ghost
            onClick={onClose}
            disabled={submitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="submit"
            form="project-form"
            variant="primary"
            disabled={
              submitting || !slugValid || !hasProjectName(name) || !hasAnySection
            }
            loading={submitting}
          >
            {isEdit ? t("common.save") : t("common.create")}
          </Button>
        </>
      }
    >
      <Tabs
        items={tabs}
        activeId={activeTab}
        onChange={(id: string) => setActiveTab(id as FormTab)}
        variant="underline"
        size="sm"
      />

      <form
        id="project-form"
        onSubmit={handleSubmit}
        className="mt-4 space-y-4"
        noValidate
      >
        {activeTab === "general" && (
          <GeneralFields
            slug={slug}
            slugValid={slugValid}
            onSlug={setSlug}
            name={name}
            onName={setName}
            description={description}
            onDescription={setDescription}
            color={color}
            onColor={setColor}
            icon={icon}
            onIcon={setIcon}
            dashboardEnabled={dashboardEnabled}
            onDashboardEnabled={setDashboardEnabled}
            maestrosEnabled={maestrosEnabled}
            onMaestrosEnabled={setMaestrosEnabled}
            filesEnabled={filesEnabled}
            onFilesEnabled={setFilesEnabled}
            hasAnySection={hasAnySection}
            submitting={submitting}
            isEdit={isEdit}
          />
        )}
        {activeTab === "maestros" && (
          <MaestrosConfigFields
            config={masterFilesConfig}
            onChange={setMasterFilesConfig}
            disabled={submitting}
          />
        )}
        {activeTab === "archivos" && (
          <ArchivosConfigFields
            config={filesConfig}
            onChange={setFilesConfig}
            disabled={submitting}
          />
        )}
        {activeTab === "data" && project && (
          <ProjectDataFields project={project} onSaved={onSaved} />
        )}
        {activeTab === "loader" && project && (
          <LoaderScheduleFields project={project} />
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark"
          >
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ── Tab: General ─────────────────────────────────────────

interface GeneralFieldsProps {
  slug: string;
  slugValid: boolean;
  onSlug: (next: string) => void;
  /** Bilingual-aware (PR-1): the column may hold JSON ``{"en","es"}``. */
  name: DisplayLabel;
  onName: (next: DisplayLabel) => void;
  description: string;
  onDescription: (next: string) => void;
  color: string;
  onColor: (next: string) => void;
  icon: string;
  onIcon: (next: string) => void;
  dashboardEnabled: boolean;
  onDashboardEnabled: (next: boolean) => void;
  maestrosEnabled: boolean;
  onMaestrosEnabled: (next: boolean) => void;
  filesEnabled: boolean;
  onFilesEnabled: (next: boolean) => void;
  hasAnySection: boolean;
  submitting: boolean;
  isEdit: boolean;
  /**
   * PR-1: the hub renders the section toggles on its right rail, so
   * it hides them here. The create page keeps the inline default.
   */
  showSectionToggles?: boolean;
  /**
   * Manual sidebar ordering (migration 026). Only the hub's config
   * form passes these — the create page leaves the DB default (0).
   * Lower sorts first; ties fall back to name.
   */
  orderIndex?: number;
  onOrderIndex?: (next: number) => void;
}

export function GeneralFields({
  slug,
  slugValid,
  onSlug,
  name,
  onName,
  description,
  onDescription,
  color,
  onColor,
  icon,
  onIcon,
  dashboardEnabled,
  onDashboardEnabled,
  maestrosEnabled,
  onMaestrosEnabled,
  filesEnabled,
  onFilesEnabled,
  hasAnySection,
  submitting,
  isEdit,
  showSectionToggles = true,
  orderIndex,
  onOrderIndex,
}: GeneralFieldsProps): JSX.Element {
  const { t } = useT();
  return (
    <>
      <Input
        label={t("projects.slug")}
        value={slug}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onSlug(e.target.value)
        }
        placeholder="my-project"
        autoFocus={!isEdit}
        disabled={submitting || isEdit}
        helperText={isEdit ? t("projects.slugLocked") : undefined}
        fullWidth
        error={
          !slugValid && slug.length > 0
            ? t("projects.slugInvalid")
            : undefined
        }
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-neutral-700">
          {t("projects.name")}
        </label>
        {/* PR-1: el valor puede ser un LocalizedString JSON — editarlo
            crudo mostraba el JSON literal en el form (bug visto en stg). */}
        <BilingualInput value={name} onChange={onName} />
      </div>

      <Input
        label={t("projects.description")}
        value={description}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onDescription(e.target.value)
        }
        placeholder=""
        disabled={submitting}
        fullWidth
      />

      {/* Was the kit's Input with type="color": that component styles a
          TEXT box, and a native colour input inside it collapses into a
          thin strip (audit B11, "se ve como una línea"). */}
      <ColorField
        label={t("projects.color")}
        value={color}
        onChange={onColor}
        placeholder={brand.primary.DEFAULT}
        disabled={submitting}
      />

      <div>
        <label className="mb-1.5 block text-sm font-medium text-neutral-700">
          {t("projects.icon")}
        </label>
        <IconPicker value={icon} onChange={onIcon} disabled={submitting} />
      </div>

      {orderIndex !== undefined && onOrderIndex !== undefined && (
        <div>
          <Input
            label={t("projects.orderIndex")}
            value={String(orderIndex)}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const parsed = Number(e.target.value);
              if (Number.isInteger(parsed)) onOrderIndex(parsed);
            }}
            disabled={submitting}
            fullWidth
            type="number"
          />
          <p className="mt-1 text-xs text-neutral-500">
            {t("projects.orderIndex.help")}
          </p>
        </div>
      )}

      {showSectionToggles && (
        <SectionToggles
          dashboardEnabled={dashboardEnabled}
          onDashboardEnabled={onDashboardEnabled}
          maestrosEnabled={maestrosEnabled}
          onMaestrosEnabled={onMaestrosEnabled}
          filesEnabled={filesEnabled}
          onFilesEnabled={onFilesEnabled}
          hasAnySection={hasAnySection}
          submitting={submitting}
        />
      )}
    </>
  );
}

// ── Section toggles (extraídos para el rail del hub — PR-1) ─────────

export interface SectionTogglesProps {
  dashboardEnabled: boolean;
  onDashboardEnabled: (next: boolean) => void;
  maestrosEnabled: boolean;
  onMaestrosEnabled: (next: boolean) => void;
  filesEnabled: boolean;
  onFilesEnabled: (next: boolean) => void;
  hasAnySection: boolean;
  submitting: boolean;
}

export function SectionToggles({
  dashboardEnabled,
  onDashboardEnabled,
  maestrosEnabled,
  onMaestrosEnabled,
  filesEnabled,
  onFilesEnabled,
  hasAnySection,
  submitting,
}: SectionTogglesProps): JSX.Element {
  const { t } = useT();
  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-neutral-700">
        {t("projects.toggles.title")}
      </label>
      <p className="mb-2 text-xs text-neutral-500">
        {t("projects.toggles.subtitle")}
      </p>
      <div className="space-y-2">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={dashboardEnabled}
            onChange={(e) => onDashboardEnabled(e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 rounded border-neutral-300"
          />
          <span>{t("projects.toggles.dashboard")}</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={maestrosEnabled}
            onChange={(e) => onMaestrosEnabled(e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 rounded border-neutral-300"
          />
          <span>{t("projects.toggles.maestros")}</span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filesEnabled}
            onChange={(e) => onFilesEnabled(e.target.checked)}
            disabled={submitting}
            className="h-4 w-4 rounded border-neutral-300"
          />
          <span>{t("projects.toggles.files")}</span>
        </label>
      </div>
      {!hasAnySection && (
        <p className="mt-2 text-xs text-error-dark">
          {t("projects.toggles.requireOne")}
        </p>
      )}
    </div>
  );
}

// ── Tab: Maestros config ─────────────────────────────────

interface MaestrosConfigFieldsProps {
  config: MasterFilesConfig;
  onChange: (next: MasterFilesConfig) => void;
  disabled: boolean;
}

export function MaestrosConfigFields({
  config,
  onChange,
  disabled,
}: MaestrosConfigFieldsProps): JSX.Element {
  const { t } = useT();
  const patch = (next: Partial<MasterFilesConfig>): void => {
    onChange({ ...config, ...next });
  };
  return (
    <>
      <p className="text-xs text-neutral-500">
        {t("projects.form.maestros.intro")}
      </p>

      <FieldLabel>{t("projects.form.maestros.pageTitle")}</FieldLabel>
      <BilingualInput
        value={config.title as DisplayLabel}
        onChange={(next) => patch({ title: next })}
        placeholder={t("projects.form.maestros.pageTitlePlaceholder")}
        disabled={disabled}
      />

      <FieldLabel>{t("projects.form.maestros.subtitle")}</FieldLabel>
      <BilingualInput
        value={config.subtitle as DisplayLabel}
        onChange={(next) => patch({ subtitle: next })}
        placeholder={t("projects.form.maestros.subtitlePlaceholder")}
        disabled={disabled}
      />

      <FieldLabel>{t("projects.form.maestros.emptyMessage")}</FieldLabel>
      <BilingualInput
        value={config.empty_message as DisplayLabel}
        onChange={(next) => patch({ empty_message: next })}
        placeholder={t("projects.form.maestros.emptyMessagePlaceholder")}
        disabled={disabled}
        multiline
      />

      <p className="text-xs text-neutral-500">
        {t("projects.form.maestros.prefixesNote")}
      </p>
    </>
  );
}

// ── Tab: Archivos config ─────────────────────────────────

interface ArchivosConfigFieldsProps {
  config: FilesConfig;
  onChange: (next: FilesConfig) => void;
  disabled: boolean;
}

const ALLOWED_TYPE_OPTIONS: ReadonlyArray<string> = [
  "csv",
  "xlsx",
  "parquet",
  "txt",
  "json",
  "pdf",
  "tsv",
];

export function ArchivosConfigFields({
  config,
  onChange,
  disabled,
}: ArchivosConfigFieldsProps): JSX.Element {
  const { t } = useT();
  const patch = (next: Partial<FilesConfig>): void => {
    onChange({ ...config, ...next });
  };
  const allowed = new Set(config.allowed_types ?? []);
  const toggleType = (type: string): void => {
    const next = new Set(allowed);
    if (next.has(type)) next.delete(type);
    else next.add(type);
    patch({ allowed_types: Array.from(next).sort() });
  };

  return (
    <>
      <p className="text-xs text-neutral-500">
        {t("projects.form.archivos.intro")}
      </p>

      <FieldLabel>{t("projects.form.archivos.pageTitle")}</FieldLabel>
      <BilingualInput
        value={config.title as DisplayLabel}
        onChange={(next) => patch({ title: next })}
        placeholder={t("projects.form.archivos.pageTitlePlaceholder")}
        disabled={disabled}
      />

      <FieldLabel>{t("projects.form.archivos.subtitle")}</FieldLabel>
      <BilingualInput
        value={config.subtitle as DisplayLabel}
        onChange={(next) => patch({ subtitle: next })}
        placeholder={t("projects.form.archivos.subtitlePlaceholder")}
        disabled={disabled}
      />

      <FieldLabel>{t("projects.form.archivos.emptyMessage")}</FieldLabel>
      <BilingualInput
        value={config.empty_message as DisplayLabel}
        onChange={(next) => patch({ empty_message: next })}
        placeholder={t("projects.form.archivos.emptyMessagePlaceholder")}
        disabled={disabled}
        multiline
      />

      <Input
        label={t("projects.form.archivos.s3RootPath")}
        value={config.s3_root_path ?? ""}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          patch({ s3_root_path: e.target.value || undefined })
        }
        placeholder="s3://my-bucket/hp-portal/files"
        disabled={disabled}
        fullWidth
      />

      <div>
        <FieldLabel>{t("projects.form.archivos.allowedTypes")}</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {ALLOWED_TYPE_OPTIONS.map((type) => {
            const active = allowed.has(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => toggleType(type)}
                disabled={disabled}
                aria-pressed={active}
                className={`rounded-md border px-2 py-1 text-xs font-medium ${ active ? "border-primary bg-primary-soft text-primary-ink" : "border-neutral-200 bg-white text-neutral-500 hover:border-neutral-300" }`}
              >
                {type}
              </button>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          {t("projects.form.archivos.allowedTypesNote")}
        </p>
      </div>

      <Input
        label={t("projects.form.archivos.maxSize")}
        value={String(config.max_file_size_mb ?? "")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const raw = e.target.value.trim();
          const num = raw === "" ? undefined : Math.max(1, Math.floor(Number(raw)));
          patch({
            max_file_size_mb: Number.isFinite(num as number) ? num : undefined,
          });
        }}
        placeholder="50"
        disabled={disabled}
        fullWidth
        type="number"
      />

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={Boolean(config.allow_upload)}
          onChange={(e) => patch({ allow_upload: e.target.checked })}
          disabled={disabled}
          className="h-4 w-4 rounded border-neutral-300"
        />
        <span>{t("projects.form.archivos.allowUpload")}</span>
      </label>
    </>
  );
}

function FieldLabel({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <label className="block text-sm font-medium text-neutral-700">
      {children}
    </label>
  );
}


// ── Tab: Data (iter 23) ───────────────────────────────────

interface ProjectDataFieldsProps {
  project: Project;
  onSaved: (project: Project) => void;
  /** Bumped by the connections panel above after a create / edit /
      delete, so the picker below re-reads the list instead of staying
      empty until the operator reloads the whole page. */
  connectionsVersion?: number;
}

/**
 * Project-level data source admin: declare the S3 URL of the project's
 * consolidated parquet (or duckdb_attach file) and trigger reloads.
 *
 * Owns its own action buttons (Save / Reload / Clear) — the modal's
 * footer Save handles general/maestros/archivos, which use the bulk
 * ``projectsApi.update`` PUT. Data source mutations are immediate
 * via dedicated endpoints so the user gets per-action feedback
 * (reload duration, row count, broken-view warnings).
 */
/** Human-readable byte size for the staged-file summary. */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

interface UploadedDataSourceFieldsProps {
  projectSlug: string;
  kind: DataSourceKind;
  upload: ProjectUploadResponse | null;
  sheet: string;
  onSheetChange: (next: string) => void;
  hasHeader: boolean;
  onHasHeaderChange: (next: boolean) => void;
  allVarchar: boolean;
  onAllVarcharChange: (next: boolean) => void;
  delimiter: string;
  onDelimiterChange: (next: string) => void;
  uploading: boolean;
  disabled: boolean;
  onUpload: (file: File) => void;
}

/**
 * CSV / Excel data source panel (Plan C phase 4).
 *
 * Carries the IN-APP GUIDE for the half of this flow that happens
 * OUTSIDE this web app — producing an accepted file from Excel, Google
 * Sheets or LibreOffice. Plan C goal #6: anything the customer has to
 * do in their own system gets numbered steps and the exact menu path,
 * not a link and a shrug.
 */
function UploadedDataSourceFields({
  projectSlug,
  kind,
  upload,
  sheet,
  onSheetChange,
  hasHeader,
  onHasHeaderChange,
  allVarchar,
  onAllVarcharChange,
  delimiter,
  onDelimiterChange,
  uploading,
  disabled,
  onUpload,
}: UploadedDataSourceFieldsProps): JSX.Element {
  const { t } = useT();
  const isExcel = kind === "excel";
  const sheets = upload?.sheets ?? [];
  const needsSheet = isExcel && !sheet;
  const guideSteps = [1, 2, 3, 4, 5, 6, 7, 8, 9];

  return (
    <div className="space-y-3 rounded-lg border border-neutral-200 bg-neutral-50/70 p-3">
      <div className="flex items-start gap-2">
        <FileSpreadsheet size={15} className="mt-0.5 text-neutral-500" />
        <div>
          <p className="text-xs font-medium text-neutral-700">
            {t("projects.form.data.upload.title")}
          </p>
          <p className="text-xs text-neutral-500">
            {t("projects.form.data.upload.subtitle")}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-neutral-300 bg-white px-2.5 py-1.5 text-xs text-neutral-700 hover:bg-neutral-50">
          <Upload size={13} />
          {upload
            ? t("projects.form.data.upload.replaceBtn")
            : t("projects.form.data.upload.chooseBtn")}
          <input
            type="file"
            accept=".csv,.tsv,.xlsx"
            className="hidden"
            disabled={disabled || uploading}
            onChange={(e) => {
              const picked = e.target.files?.[0];
              // Reset the input so picking the SAME filename twice
              // still fires a change event (a re-upload after fixing
              // the file in Excel is the common case).
              e.target.value = "";
              if (picked) onUpload(picked);
            }}
          />
        </label>
        {uploading && (
          <span className="inline-flex items-center gap-1.5 text-xs text-neutral-500">
            <Spinner size={12} />
            {t("projects.form.data.upload.uploading")}
          </span>
        )}
      </div>

      {upload && (
        <div className="rounded-md border border-neutral-200 bg-white px-2.5 py-2 text-xs text-neutral-600">
          <div className="font-mono text-xs text-neutral-800">
            {upload.filename}
          </div>
          <div className="mt-0.5">
            {formatBytes(upload.size_bytes)}
            {upload.row_count !== null && (
              <>
                {" · "}
                {t("projects.form.data.upload.rowsRead", {
                  rows: upload.row_count.toLocaleString(),
                  columns: Object.keys(upload.columns).length,
                })}
              </>
            )}
          </div>
          <div className="mt-0.5 text-neutral-500">
            {t("projects.form.data.upload.stagedNote")}
          </div>
        </div>
      )}

      {isExcel && (
        <div>
          <FieldLabel>{t("projects.form.data.upload.sheetLabel")}</FieldLabel>
          {sheets.length > 0 ? (
            <Select
              className="mt-1"
              options={[
                {
                  value: "",
                  label: t("projects.form.data.upload.sheetPlaceholder"),
                },
                ...sheets.map((name) => ({ value: name, label: name })),
              ]}
              value={sheet}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                onSheetChange(e.target.value)
              }
              disabled={disabled || uploading}
              fullWidth
            />
          ) : (
            <p className="mt-1 text-xs text-neutral-500">
              {t("projects.form.data.upload.sheetNoWorkbook")}
            </p>
          )}
          <p className="mt-1 text-xs text-neutral-500">
            {t("projects.form.data.upload.sheetHint")}
          </p>
          {needsSheet && sheets.length > 0 && (
            <p className="mt-1 flex items-center gap-1 text-xs text-danger-600">
              <AlertCircle size={12} />
              {t("projects.form.data.upload.sheetRequired")}
            </p>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {!isExcel && (
          <div>
            <FieldLabel>
              {t("projects.form.data.upload.delimiterLabel")}
            </FieldLabel>
            <input
              value={delimiter === "\t" ? "\\t" : delimiter}
              onChange={(e) =>
                onDelimiterChange(
                  e.target.value === "\\t" ? "\t" : e.target.value,
                )
              }
              placeholder={t("projects.form.data.upload.delimiterPlaceholder")}
              disabled={disabled || uploading}
              className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("projects.form.data.upload.delimiterHint")}
            </p>
          </div>
        )}
        <div className="space-y-2 pt-1">
          <label className="flex items-center gap-2 text-xs text-neutral-700">
            <input
              type="checkbox"
              checked={hasHeader}
              onChange={(e) => onHasHeaderChange(e.target.checked)}
              disabled={disabled || uploading}
            />
            {t("projects.form.data.upload.headerLabel")}
          </label>
          <label className="flex items-center gap-2 text-xs text-neutral-700">
            <input
              type="checkbox"
              checked={allVarchar}
              onChange={(e) => onAllVarcharChange(e.target.checked)}
              disabled={disabled || uploading}
            />
            {t("projects.form.data.upload.allVarcharLabel")}
          </label>
          <p className="text-xs text-neutral-500">
            {t("projects.form.data.upload.allVarcharHint")}
          </p>
        </div>
      </div>

      <Disclosure summary={t("projects.form.data.upload.guideTitle")}>
        <ol className="mt-2 list-decimal space-y-1.5 pl-4 text-xs leading-relaxed text-neutral-600">
          {guideSteps.map((n) => (
            <li key={n}>
              {t(`projects.form.data.upload.guideStep${n}`, {
                slug: projectSlug,
              })}
            </li>
          ))}
        </ol>
      </Disclosure>
    </div>
  );
}

export function ProjectDataFields({
  project,
  onSaved,
  connectionsVersion = 0,
}: ProjectDataFieldsProps): JSX.Element {
  const { t } = useT();
  const initialDs = (project.data_source ?? {}) as Partial<{
    kind: DataSourceKind;
    url: string;
    table: string;
    source_table: string;
    alias: string;
    connection_id: string;
    // Plan C phase 4 — uploaded dataset (kind csv / excel).
    upload_id: string;
    ext: string;
    filename: string;
    size_bytes: number;
    uploaded_at: string;
    sheet: string;
    sheets: string[];
    header: boolean;
    all_varchar: boolean;
    delimiter: string;
    // Parquet: columns the raw table is sorted by when written (storage
    // only). No control edits it; it is set by script and carried as is.
    order_by: string[];
  }>;

  const [kind, setKind] = useState<DataSourceKind>(
    initialDs.kind ?? "parquet",
  );
  const [url, setUrl] = useState(initialDs.url ?? "");
  const [tableName, setTableName] = useState(
    initialDs.table ?? initialDs.source_table ?? "",
  );
  const [alias, setAlias] = useState(initialDs.alias ?? "");
  // Track B1 — which connection supplies the credentials. Stored
  // inside data_source JSONB, not as a projects column.
  const [connectionId, setConnectionId] = useState(
    initialDs.connection_id ?? "",
  );
  const [connections, setConnections] = useState<ConnectionResponse[]>([]);

  // ── Plan C phase 4 — uploaded dataset (csv / excel) ───────────────
  // ``upload`` is the STAGED or currently-saved file. It is rebuilt
  // from ``data_source`` on mount so re-opening the modal shows what is
  // live, and replaced wholesale by the response of a new upload.
  const [upload, setUpload] = useState<ProjectUploadResponse | null>(
    initialDs.upload_id
      ? {
          upload_id: initialDs.upload_id,
          kind: initialDs.kind ?? "csv",
          ext: initialDs.ext ?? "",
          filename: initialDs.filename ?? "",
          size_bytes: initialDs.size_bytes ?? 0,
          uploaded_at: initialDs.uploaded_at ?? "",
          sheet: initialDs.sheet ?? null,
          sheets: initialDs.sheets ?? [],
          row_count: null,
          columns: {},
          delimiter: initialDs.delimiter ?? "",
        }
      : null,
  );
  const [sheet, setSheet] = useState(initialDs.sheet ?? "");
  const [hasHeader, setHasHeader] = useState(initialDs.header ?? true);
  const [allVarchar, setAllVarchar] = useState(initialDs.all_varchar ?? false);
  const [delimiter, setDelimiter] = useState(initialDs.delimiter ?? "");
  const [uploading, setUploading] = useState(false);

  const [loadedAt, setLoadedAt] = useState<string | null>(
    project.data_source_loaded_at ?? null,
  );
  const [rows, setRows] = useState<number>(project.data_source_rows ?? 0);
  const [schemaMap, setSchemaMap] = useState<
    Record<string, string | Record<string, string>>
  >(project.data_source_schema ?? {});

  const [saving, setSaving] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadResult, setReloadResult] =
    useState<ProjectDataSourceReloadResponse | null>(null);

  // Track B1 — populate the connection picker. A failure here is
  // surfaced (not swallowed): without the list the operator cannot
  // tell whether a connection exists, and saving would 400 anyway.
  useEffect(() => {
    let cancelled = false;
    projectsApi.connections
      .list(project.slug)
      .then((out) => {
        if (!cancelled) setConnections(out.connections);
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug, connectionsVersion]);

  const isUpload = isUploadKind(kind);
  // What "the project has something to reload" means depends on the
  // kind: parquet / duckdb address a URL, postgres / api address a
  // connection, csv / excel address an uploaded file. The check used to
  // be ``kind && url``, which disabled Reload for every URL-less kind.
  const hasConfig = Boolean(
    initialDs.kind &&
      (initialDs.url ||
        initialDs.upload_id ||
        initialDs.connection_id ||
        initialDs.kind === "postgres" ||
        initialDs.kind === "api"),
  );
  // A remote source cannot be saved without a connection: the
  // process-global object-store secret was removed in track B1 and
  // there is no anonymous fallback, so the reload would fail loudly.
  // An uploaded file needs none — it is already on this tenant's
  // volume.
  const needsConnection =
    kind === "postgres" || kind === "api" || url.trim().startsWith("s3://");
  // postgres / api are "coming soon" in this form (DEC-8): onSave only
  // sends kind/url/table/alias/connection_id, and the connectors need
  // host/port/database (postgres) or records_path/auth/pagination (api)
  // in the data_source — a save from here is a guaranteed 400, or it
  // silently strips an api source's settings. So the options render
  // disabled with the "Pronto" badge, and a project that already uses
  // one sees it read-only with the reason.
  const isSoonKind = kind === "postgres" || kind === "api";
  const canSave = !isSoonKind && (isUpload
    ? Boolean(upload?.upload_id) && (kind !== "excel" || sheet.trim().length > 0)
    : (kind === "parquet"
        ? url.trim().endsWith(".parquet")
        : kind === "duckdb_attach"
          ? url.trim().endsWith(".duckdb") && tableName.trim().length > 0
          : true) &&
      url.trim().length > 0 &&
      (!needsConnection || connectionId.trim().length > 0));

  // WHY the button is dead, in the operator's own words.
  //
  // Every clause of `canSave` is correct — an `s3://` URL really does
  // need a connection now that B1 removed the anonymous object-store
  // read. What was missing is that the rule was INVISIBLE: the operator
  // typed a valid URL, watched a greyed-out button, and had no way to
  // learn that the blocker was a credential this project cannot see.
  // The fix is to say it, not to relax the rule.
  const blockedReason: string | null = (() => {
    if (canSave) return null;
    if (isSoonKind)
      return t("projects.form.data.blockedKindSoon", {
        kind: t(`projects.form.data.kind.${kind}`),
      });
    if (isUpload) {
      if (!upload?.upload_id) return t("projects.form.data.blockedUpload");
      if (kind === "excel" && sheet.trim().length === 0)
        return t("projects.form.data.blockedSheet");
      return null;
    }
    if (url.trim().length === 0) return t("projects.form.data.blockedUrl");
    if (kind === "parquet" && !url.trim().endsWith(".parquet"))
      return t("projects.form.data.blockedParquetExt");
    if (kind === "duckdb_attach" && !url.trim().endsWith(".duckdb"))
      return t("projects.form.data.blockedDuckdbExt");
    if (kind === "duckdb_attach" && tableName.trim().length === 0)
      return t("projects.form.data.blockedTable");
    if (needsConnection && connectionId.trim().length === 0)
      return connections.length === 0
        ? t("projects.form.data.blockedNoConnections")
        : t("projects.form.data.blockedConnection");
    return null;
  })();

  const onUpload = async (file: File): Promise<void> => {
    setError(null);
    setUploading(true);
    try {
      const staged = await projectsApi.dataSource.upload(project.slug, file);
      setUpload(staged);
      setKind(staged.kind);
      setSheet(staged.sheet ?? "");
      setDelimiter(staged.delimiter);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  };

  const onSave = async (): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      const result = await projectsApi.dataSource.put(project.slug, {
        kind,
        ...(isUpload
          ? {
              upload_id: upload?.upload_id,
              ext: upload?.ext,
              filename: upload?.filename,
              size_bytes: upload?.size_bytes,
              uploaded_at: upload?.uploaded_at,
              header: hasHeader,
              all_varchar: allVarchar,
              ...(kind === "csv" ? { delimiter } : {}),
              ...(kind === "excel"
                ? { sheet: sheet.trim(), sheets: upload?.sheets ?? [] }
                : {}),
            }
          : {}),
        url: isUpload ? undefined : url.trim() || undefined,
        table: tableName.trim() || undefined,
        alias: isUpload ? undefined : alias.trim() || undefined,
        connection_id: isUpload
          ? undefined
          : connectionId.trim() || undefined,
        // The backend stores the normalized dict, so a key this form does
        // not send is dropped: carry the stored sort order of a parquet
        // source instead of silently losing it on save.
        ...(kind === "parquet" &&
        initialDs.kind === "parquet" &&
        Array.isArray(initialDs.order_by)
          ? { order_by: initialDs.order_by }
          : {}),
      });
      setLoadedAt(result.data_source_loaded_at);
      setRows(result.data_source_rows);
      setSchemaMap(result.data_source_schema);
      // hasConfig (Reload / Clear) and the Auto-refresh tab derive from
      // the PARENT's `project.data_source`; the PUT response does not
      // carry it, so re-read the project like onReload / onClear do.
      const refreshed = await projectsApi.get(project.slug);
      onSaved(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onReload = async (): Promise<void> => {
    setError(null);
    setReloading(true);
    setReloadResult(null);
    try {
      const result = await projectsApi.dataSource.reload(project.slug);
      setReloadResult(result);
      setLoadedAt(result.loaded_at);
      setRows(result.rows);
      setSchemaMap(result.schema);
      // Refresh the parent project list so the row reflects the new state
      // if the modal is closed and reopened.
      const refreshed = await projectsApi.get(project.slug);
      onSaved(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setReloading(false);
    }
  };

  const onClear = async (): Promise<void> => {
    if (
      !window.confirm(
        t("projects.form.data.clearConfirm"),
      )
    )
      return;
    setError(null);
    setSaving(true);
    try {
      const result = await projectsApi.dataSource.clear(project.slug);
      setKind("parquet");
      setUrl("");
      setTableName("");
      setAlias("");
      setConnectionId("");
      setUpload(null);
      setSheet("");
      setHasHeader(true);
      setAllVarchar(false);
      setDelimiter("");
      setLoadedAt(result.data_source_loaded_at);
      setRows(result.data_source_rows);
      setSchemaMap(result.data_source_schema);
      setReloadResult(null);
      const refreshed = await projectsApi.get(project.slug);
      onSaved(refreshed);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const schemaEntries = Object.entries(schemaMap);
  // Detect shape: parquet → flat {col: type}; duckdb_attach → nested
  // {table: {col: type}}. The first entry's value type tells us which
  // shape we're looking at (empty map → render nothing either way).
  const isNestedSchema =
    schemaEntries.length > 0 &&
    typeof schemaEntries[0][1] === "object" &&
    schemaEntries[0][1] !== null;
  const columnCount = isNestedSchema
    ? schemaEntries.reduce(
        (total, [, cols]) =>
          total + Object.keys(cols as Record<string, string>).length,
        0,
      )
    : schemaEntries.length;
  const viewErrors = Object.entries(reloadResult?.view_results ?? {}).filter(
    ([, msg]) => msg !== null,
  );

  return (
    <div className="space-y-4">
      <p className="text-xs text-neutral-500">
        {t("projects.form.data.intro")}
      </p>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div>
          {/* The four first options used to be hardcoded English —
              "parquet (OBT)", "duckdb (attach)", "postgres", "api (JSON)"
              — sitting inside a Spanish form next to two that were
              translated. */}
          <Select
            label={t("projects.form.data.kindLabel")}
            value={kind}
            onChange={(e) => setKind(e.target.value as DataSourceKind)}
            disabled={saving || reloading}
            fullWidth
            options={[
              { value: "parquet", label: t("projects.form.data.kind.parquet") },
              {
                value: "duckdb_attach",
                label: t("projects.form.data.kind.duckdb_attach"),
              },
              // Same "Pronto" pattern as the unfinished send channels:
              // visible, disabled, labelled — never offered as if it
              // worked (DEC-8).
              {
                value: "postgres",
                label: `${t("projects.form.data.kind.postgres")} · ${t("projects.form.data.kindSoon")}`,
                disabled: true,
              },
              {
                value: "api",
                label: `${t("projects.form.data.kind.api")} · ${t("projects.form.data.kindSoon")}`,
                disabled: true,
              },
              { value: "csv", label: t("projects.form.data.kindCsv") },
              { value: "excel", label: t("projects.form.data.kindExcel") },
            ]}
          />
        </div>
        {!isUpload && (
          <div className="md:col-span-2">
            <FieldLabel>
              {t("projects.form.data.urlLabel")}
            </FieldLabel>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={
                kind === "parquet"
                  ? "s3://bucket/path/data.parquet"
                  : "s3://bucket/path/db.duckdb"
              }
              disabled={saving || reloading}
              className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
            />
          </div>
        )}
      </div>

      {isUpload && (
        <UploadedDataSourceFields
          projectSlug={project.slug}
          kind={kind}
          upload={upload}
          sheet={sheet}
          onSheetChange={setSheet}
          hasHeader={hasHeader}
          onHasHeaderChange={setHasHeader}
          allVarchar={allVarchar}
          onAllVarcharChange={setAllVarchar}
          delimiter={delimiter}
          onDelimiterChange={setDelimiter}
          uploading={uploading}
          disabled={saving || reloading}
          onUpload={(file) => void onUpload(file)}
        />
      )}

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {(kind === "parquet" || isUpload) && (
          <div>
            <FieldLabel>
              {t("projects.form.data.tableLabel")}
            </FieldLabel>
            <input
              value={tableName}
              onChange={(e) => setTableName(e.target.value)}
              placeholder={`project_raw_${project.slug}`}
              disabled={saving || reloading}
              className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
            />
            <p className="mt-1 text-xs text-neutral-500">
              {t("projects.form.data.tableHint", {
                slug: project.slug,
              })}
            </p>
          </div>
        )}
        {kind === "duckdb_attach" && (
          <>
            <div>
              <FieldLabel>
                {t("projects.form.data.attachTableLabel")}
              </FieldLabel>
              <input
                value={tableName}
                onChange={(e) => setTableName(e.target.value)}
                placeholder="main.sales"
                disabled={saving || reloading}
                className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
              />
              <p className="mt-1 text-xs text-neutral-500">
                {t("projects.form.data.attachTableHint", {
                  slug: project.slug,
                })}
              </p>
            </div>
            <div>
              <FieldLabel>
                {t("projects.form.data.aliasLabel")}
              </FieldLabel>
              <input
                value={alias}
                onChange={(e) => setAlias(e.target.value)}
                placeholder={`proj_${project.slug}`}
                disabled={saving || reloading}
                className="mt-1 block rounded-[10px] border border-control bg-white px-3 py-2 placeholder:text-neutral-500 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary w-full font-mono text-xs"
              />
            </div>
          </>
        )}
        {/* An uploaded file is already on this tenant's own volume, so
            it needs no credential — showing a connection picker would
            imply otherwise. */}
        {!isUpload && (
          <Select
            label={t("projects.form.data.connectionLabel")}
            value={connectionId}
            onChange={(e) => setConnectionId(e.target.value)}
            disabled={saving || reloading}
            helperText={t("projects.form.data.connectionHint")}
            fullWidth
            options={[
              { value: "", label: t("projects.form.data.connectionNone") },
              ...connections.map((c) => ({
                value: c.id,
                label: `${c.name} (${c.kind})`,
              })),
            ]}
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="primary"
          size="sm"
          onClick={() => void onSave()}
          disabled={!canSave || saving || reloading}
          loading={saving}
        >
          {isUpload
            ? t("projects.form.data.saveUploadBtn")
            : t("projects.form.data.saveBtn")}
        </Button>
        <Button
          ghost
          size="sm"
          onClick={() => void onReload()}
          disabled={!hasConfig || saving || reloading}
          loading={reloading}
          leftIcon={<RefreshCw size={13} />}
        >
          {isUpload
            ? t("projects.form.data.reloadUploadBtn")
            : t("projects.form.data.reloadBtn")}
        </Button>
        {hasConfig && (
          <Button
            variant="danger"
            ghost
            size="sm"
            onClick={() => void onClear()}
            disabled={saving || reloading}
            leftIcon={<Trash2 size={13} />}
          >
            {t("projects.form.data.clearBtn")}
          </Button>
        )}
      </div>

      {blockedReason && (
        <div className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs text-neutral-700">
          <AlertCircle size={14} className="mt-px shrink-0 text-warning-dark" />
          <span>{blockedReason}</span>
        </div>
      )}

      {error && (
        <div className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-xs text-error-dark">
          {error}
        </div>
      )}

      {/* Audit state */}
      <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs">
        <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
          <div>
            <div className="text-xs text-neutral-500">
              {t("projects.form.data.loadedAt")}
            </div>
            <div className="mt-0.5 font-mono">
              {loadedAt ? formatRelative(loadedAt) : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">
              {t("projects.form.data.rows")}
            </div>
            <div className="mt-0.5 font-mono">
              {rows ? rows.toLocaleString() : "—"}
            </div>
          </div>
          <div>
            <div className="text-xs text-neutral-500">
              {isNestedSchema
                ? t("projects.form.data.tables")
                : t("projects.form.data.columns")}
            </div>
            <div className="mt-0.5 font-mono">
              {isNestedSchema
                ? schemaEntries.length || "—"
                : columnCount || "—"}
              {isNestedSchema && columnCount ? (
                <span className="ml-1 text-xs text-neutral-500">
                  ({columnCount}{" "}
                  {t("projects.form.data.columnsShort")}
                  )
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {schemaEntries.length > 0 && !isNestedSchema && (
        <Disclosure
          summary={`${t("projects.form.data.schemaTitle", {
            count: schemaEntries.length,
          })} (${schemaEntries.length})`}
        >
          <div className="grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3">
            {schemaEntries.map(([col, type]) => (
              <div
                key={col}
                className="flex items-center justify-between rounded border border-neutral-200 bg-neutral-50 px-2 py-1"
              >
                <span className="truncate font-mono text-xs text-neutral-800">
                  {col}
                </span>
                <span className="ml-2 shrink-0 font-mono text-xs text-neutral-500">
                  {String(type)}
                </span>
              </div>
            ))}
          </div>
        </Disclosure>
      )}

      {schemaEntries.length > 0 && isNestedSchema && (
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-neutral-600">
            {t("projects.form.data.catalogTitle", {
              count: schemaEntries.length,
            })}{" "}
            ({schemaEntries.length})
          </h4>
          {schemaEntries.map(([tableName, cols]) => {
            const colEntries = Object.entries(
              cols as Record<string, string>,
            );
            return (
              <Disclosure
                key={tableName}
                summary={
                  <>
                    <code className="font-mono font-semibold text-neutral-800">
                      {tableName}
                    </code>
                    <span className="ml-2 font-normal text-neutral-500">
                      ({colEntries.length}{" "}
                      {t("projects.form.data.columnsShort")})
                    </span>
                  </>
                }
              >
                <div className="grid grid-cols-1 gap-1 md:grid-cols-2 lg:grid-cols-3">
                  {colEntries.map(([col, type]) => (
                    <div
                      key={col}
                      className="flex items-center justify-between rounded border border-neutral-200 bg-neutral-50 px-2 py-1"
                    >
                      <span className="truncate font-mono text-xs text-neutral-800">
                        {col}
                      </span>
                      <span className="ml-2 shrink-0 font-mono text-xs text-neutral-500">
                        {type}
                      </span>
                    </div>
                  ))}
                </div>
              </Disclosure>
            );
          })}
        </div>
      )}

      {reloadResult && viewErrors.length > 0 && (
        <div className="rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs">
          <div className="font-semibold text-neutral-800">
            {t("projects.form.data.viewWarnings")}
          </div>
          <ul className="mt-1 space-y-0.5 font-mono text-xs text-neutral-700">
            {viewErrors.map(([name, msg]) => (
              <li key={name}>
                <code>{name}</code>: {msg}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
